"use client";

import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import SupportDrawer from "@/components/atoms/drawer/support-drawer";
import EditContactDetails from "@/components/molecules/quote-section/edit-quote-section/edit-contact-details";
import EditMedical from "@/components/molecules/quote-section/edit-quote-section/edit-medical";
import EditTavellers from "@/components/molecules/quote-section/edit-quote-section/edit-travellers";
import EditTrip from "@/components/molecules/quote-section/edit-quote-section/edit-trip";
import withBrand from "@/components/with-brand";
import withDashboard from "@/components/with-dashboard";
import { QuoteEdit } from "@/config/app-constant";
import { ANNUAL_MULTI_TRIP, SINGLE_TRIP } from "@/constants";
import useMediaQuery from "@/hooks/use-media-query/use-media-query";
import { useQuoteDisabled } from "@/hooks/use-quote-disabled/use-quote-disabled";
import useResetStateTravelerEdit from "@/hooks/use-reset-state-traveler-edit/user-reset-state-traveler-edit";
import { formatDate as canonicalFormatDate } from "@/libs/i18n/format-date";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { useLoading } from "@/providers/loading-provider";
import { HeaderContentType } from "@/schema/quote-page/page.schema";
import { RootState } from "@/store/root-reducer";
import { closeHelpOverlay } from "@/store/slices/help-overlay.slice";
import { setMedicalData } from "@/store/slices/medical.slice";
import { setQuestionData } from "@/store/slices/quote/quote.slice";
import { MedicalGateKeeperQuestions } from "@/types/MedicalTypes";
import { QuotePayload } from "@/types/quote/quote-data";
import { modifyQuestionQuotePayload } from "@/utils/common.utils";
// import { QuotePayload } from "@/types/quote/quote-data";

declare global {
  interface Window {
    minimizeCXoneChat?: () => void;
  }
}

function QuoteHeader({
  headerContent,
  dashboardEnabled = true,
  brand = "staysure",
}: Readonly<{
  headerContent: HeaderContentType | null;
  dashboardEnabled?: boolean;
  brand?: "staysure" | "avanti";
}>) {
  const dispatch = useDispatch();
  const isQuoteDisabled = useQuoteDisabled();
  const { current } = useWizard();
  const { t } = useWizardTranslation();
  const { resetQuestionData } = useResetStateTravelerEdit();
  const { showLoading, hideLoading } = useLoading();
  const {
    greeting = "",
    subtitle = "",
    ctaLabel = "",
    tripTitle = "",
    typeLabel = "",
    destinationLabel = "",
    datesLabel = "",
    regionLabel = "",
    cruiseLabel = "",
    policyStartLabel = "",
    travellerTitle = "",
    travellersLable = "",
    addBtnLabel = "",
    editBtnLabel = "",
    partyTypeLable = "",
    partyTypeBtnLabel = "",
    medicalTitle = "",
    medicalBtn = "",
    editMedicalBtn = "",
    contactTitle = "",
    organizerTitle = "",
    contactBtn = "",
    editOrganizerBtn = "",
    reassuranceCard1Title = "",
    reassuranceCard1Description = "",
    reassuranceCard2Title = "",
    reassuranceCard2Description = "",
    reassuranceCard3Title = "",
    reassuranceCard3Description = "",
  } = headerContent ?? {};

  const quote = useSelector((state: RootState) => state.quote);
  const searchParams = useSearchParams();
  const editPage = searchParams.get(QuoteEdit);
  const [open, setOpen] = useState(false);
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const isMobile = useMediaQuery("(max-width: 600px)");
  const isTablet = useMediaQuery("(min-width: 600px) and (max-width: 903px)");
  const isTabletRange = useMediaQuery(
    "(min-width: 601px) and (max-width: 752px)"
  );
  const isTabMinRange = useMediaQuery(
    "(min-width: 601px) and (max-width: 640px)"
  );
  const isTabMaxRange = useMediaQuery(
    "(min-width: 641px) and (max-width: 904px)"
  );
  const pathname = usePathname();
  const router = useRouter();
  const { attributes, referenceId } = quote;
  const { tripDetails, travellerDetails } = attributes;

  // Ref to track if resetQuestionData has been called to prevent duplicate API calls
  const hasResetQuestionDataRef = useRef(false);

  const travellerName = quote?.attributes?.organiser?.firstName;
  const isSimplifiedHeroBanner =
    current?.uiElementControls?.isSimplifiedHeroBanner;
  const showReassuranceTiles = current?.uiElementControls?.showReassuranceTiles;
  const iSInternationalHeaderStyle =
    current?.uiElementControls?.internationalHeaderStyle;

  const fallbackPage = useMemo(() => {
    return current?.edges?.fallbackOnError;
  }, [current?.edges?.fallbackOnError]);

  const gotoFallbackPage = useCallback(() => {
    if (fallbackPage) {
      router.push(fallbackPage);
    }
  }, [fallbackPage, router]);

  useEffect(() => {
    if (editPage) {
      dispatch(closeHelpOverlay());
      // Close CXone chat window if open
      if (typeof window !== "undefined" && window.minimizeCXoneChat) {
        window.minimizeCXoneChat();
      }
      setOpen(true);
    }
  }, [editPage, dispatch]);

  useEffect(() => {
    // This useEffect uses for reset the question data and medical data in redux store to initial state
    // otherwise when user go back to edit page and come back to quote page via browser back button, unsaved changes will be remained in redux store

    /*    dispatch(
      setQuestionData(modifyQuestionQuotePayload(quote as QuotePayload))
    ); */
    dispatch(
      setMedicalData(
        quote?.attributes?.travellerDetails
          ?.gateKeeperAnswers as MedicalGateKeeperQuestions
      )
    );
  }, [quote, dispatch]);

  // Handle date validation and reset state when both dates are invalid
  useEffect(() => {
    const handleResetWithLoading = async () => {
      // Guard to prevent multiple calls
      if (hasResetQuestionDataRef.current) return;

      hasResetQuestionDataRef.current = true;
      try {
        showLoading();
        await resetQuestionData();
      } finally {
        hideLoading();
      }
    };

    if (!tripDetails) return;

    const hasStartDate = !!tripDetails?.startDate;
    const hasEndDate = !!tripDetails?.endDate;

    // If both dates are missing, reset state (only once)
    if (!hasStartDate && !hasEndDate) {
      handleResetWithLoading();
      return;
    }

    // If at least one date exists, check validity
    if (hasStartDate && hasEndDate) {
      const startDate = new Date(tripDetails.startDate as unknown as string);
      const endDate = new Date(tripDetails.endDate as unknown as string);

      const bothDatesValid =
        !Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime());

      // If both dates are valid, reset the flag so we can detect issues again later
      if (bothDatesValid) {
        hasResetQuestionDataRef.current = false;
      } else if (
        Number.isNaN(startDate.getTime()) &&
        Number.isNaN(endDate.getTime())
      ) {
        // If both dates are invalid after parsing, reset state (only once)
        handleResetWithLoading();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripDetails?.startDate, tripDetails?.endDate]); // Only run when dates change

  let characterLimit = 40;
  if (isTabMinRange) characterLimit = 20;
  if (isTabMaxRange) characterLimit = 30;

  const formatDate = (date: Date) => canonicalFormatDate(date, "dd/MM/yy");

  const locationData = useMemo(() => {
    if (!tripDetails) return "";

    const { policyType, toLocationCountryDetails, area } = tripDetails;

    if (policyType === SINGLE_TRIP && toLocationCountryDetails?.length) {
      return toLocationCountryDetails?.length === 1
        ? toLocationCountryDetails[0]?.attributes?.countryName
        : t("multiple_countries", "Multiple countries");
    }
    if (policyType === ANNUAL_MULTI_TRIP) {
      const locationMap = {
        UK: t("uk_only", "UK only"),
        IE: t("ireland_only", "Ireland only"),
        ESP: t("spain_only", "Spain only"),
        EUROPE_LR: t("europe_with_exclusions", "Europe, with exclusions"),
        EUROPE_HR: t("europe", "Europe"),
        WORLD_LR: t("worldwide_with_exclusions", "Worldwide, with exclusions"),
        WORLD_HR: t("worldwide", "Worldwide"),
      };
      const location = area
        ? locationMap[area as keyof typeof locationMap]
        : "";
      return location;
    }
    /* istanbul ignore next */
    return ""; // this is a failsafe, it should never be reached
  }, [tripDetails, t]);

  const onCloseQuotePageOverlay = () => {
    router.replace(pathname);
  };

  const renderEditButton = () => (
    <LabelButtonWrapper
      id="quote-header-cta"
      hierarchy="tertiary"
      label={ctaLabel}
      isRightIcon={false}
      semanticColor="Dark Background"
      className={`text-[16px] font-body-font font-[600] text-surface-neutral-option-2 leading-[19.49px] !h-[24px] disabled:cursor-not-allowed ${
        isSimplifiedHeroBanner ? "" : "!p-spacing-xxs !pt-[3px]"
      }`}
      onClick={() => {
        dispatch(closeHelpOverlay());
        // Close CXone chat window if open
        if (typeof window !== "undefined" && window.minimizeCXoneChat) {
          window.minimizeCXoneChat();
        }
        dispatch(
          setQuestionData(modifyQuestionQuotePayload(quote as QuotePayload))
        );
        if (dashboardEnabled) {
          setOpen(true);
          setOpenAccordion("trip");
        } else {
          gotoFallbackPage();
        }
      }}
      isDisabled={isQuoteDisabled}
    />
  );

  const dateAndTravellers = useMemo(() => {
    if (!tripDetails) return "";

    const hasStartDate = !!tripDetails?.startDate;
    const hasEndDate = !!tripDetails?.endDate;

    // If any date is missing, hide display (reset is handled in useEffect)
    if (!hasStartDate || !hasEndDate) {
      return "";
    }

    // Create Date objects and validate
    const startDate = new Date(tripDetails.startDate as unknown as string);
    const endDate = new Date(tripDetails.endDate as unknown as string);

    // If any date is invalid after parsing, hide display (reset is handled in useEffect)
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      return "";
    }

    const travellerCount = travellerDetails?.traveller?.length ?? 0;

    let dateRange = `${formatDate(startDate)} \u002D ${formatDate(endDate)}`;
    const travellersText = `${travellerCount} ${
      travellerCount === 1
        ? t("traveller", "Traveller")
        : t("travellers", "Travellers")
    }`;

    if (tripDetails.policyType === ANNUAL_MULTI_TRIP) {
      const amtEndDate = new Date(startDate);
      amtEndDate.setFullYear(startDate.getFullYear() + 1);
      amtEndDate.setDate(startDate.getDate() - 1);
      dateRange = `${formatDate(startDate)} \u002D ${formatDate(amtEndDate)}`;
    }

    const shouldDisplayBullet =
      (!isMobile || isSimplifiedHeroBanner) &&
      locationData?.length &&
      (locationData?.length <= characterLimit || isSimplifiedHeroBanner);
    const bullet = shouldDisplayBullet ? "• " : "";

    return `${bullet}${dateRange} • ${travellersText}`;
  }, [
    tripDetails,
    travellerDetails,
    isMobile,
    locationData,
    characterLimit,
    t,
    isSimplifiedHeroBanner,
  ]);

  const renderTripDetails = () => (
    <>
      <div className="font-[600] text-dark-text-functional text-center">
        <span>{locationData}</span>
        {locationData?.length && locationData?.length > characterLimit && (
          <br />
        )}
        {!isMobile && (
          <span
            className={`text-center ${
              isSimplifiedHeroBanner
                ? "font-semibold"
                : "md:font-[400] font-[500]"
            } text-dark-text-accent-1-primary mt-spacing-xxs md:mt-0 ${
              isSimplifiedHeroBanner
                ? "text-[16px] leading-[150%]"
                : "text-[12px] md:text-[16px] leading-[16px] md:leading-[150%]"
            }`}
          >
            &nbsp;{dateAndTravellers}
          </span>
        )}
      </div>
      {isMobile && (
        <div
          className={`${
            isSimplifiedHeroBanner ? "tracking-tight" : "w-[100%]"
          } text-center ${
            isSimplifiedHeroBanner
              ? "font-semibold"
              : "md:font-[400] font-[500]"
          } text-surface-accent-1-primary ${
            isSimplifiedHeroBanner
              ? "text-[16px] leading-[150%]"
              : "mt-spacing-xxs md:mt-0 text-[12px] md:text-[16px] leading-[16px] md:leading-[150%]"
          }`}
        >
          &nbsp;{dateAndTravellers}
        </div>
      )}
    </>
  );

  const getButtonContainerClasses = () => {
    if (isMobile) return "w-full";
    if (isTabletRange) return "w-full mt-spacing-xxs";
    return "w-auto ml-auto";
  };

  const renderReassuranceCards = () => (
    <div className="w-[264px] max-w-[264px] min-h-[80px] mx-auto mb-spacing-m tablet:w-[634px] tablet:max-w-[634px] tablet:min-h-[24px] tablet:mb-4 lg:w-[772px] lg:max-w-[772px] lg:min-h-[82px]">
      <div className="grid grid-cols-1 gap-3 w-full tablet:grid-cols-3 tablet:gap-6 lg:gap-[56px]">
        <div className="flex flex-col items-start text-dark-text-on-primary lg:max-w-[220px] lg:w-[220px] gap-[8px]">
          <div className="flex items-center gap-spacing-xs">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <h3 className="text-inherit font-body-font font-semibold text-[16px] leading-normal">
              {reassuranceCard1Title}
            </h3>
          </div>
          <p className="text-inherit hidden font-body-font font-normal text-[14px] leading-normal opacity-80 lg:block">
            {reassuranceCard1Description}
          </p>
        </div>
        <div className="flex flex-col items-start text-dark-text-on-primary lg:max-w-[220px] lg:w-[220px] gap-[8px]">
          <div className="flex items-center gap-spacing-xs">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 22 22"
              fill="none"
            >
              <path
                d="M11 1L14.09 7.26L21 8.27L16 13.14L17.18 20.02L11 16.77L4.82 20.02L6 13.14L1 8.27L7.91 7.26L11 1Z"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <h3 className="text-inherit font-body-font font-semibold text-[16px] leading-normal">
              {reassuranceCard2Title}
            </h3>
          </div>
          <p className="text-inherit hidden font-body-font font-normal text-[14px] leading-normal opacity-80 lg:block">
            {reassuranceCard2Description}
          </p>
        </div>
        <div className="flex flex-col items-start text-dark-text-on-primary lg:max-w-[220px] lg:w-[220px] gap-[8px]">
          <div className="flex items-center gap-spacing-xs">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M4.8 2.30136C4.80356 2.3642 4.8268 2.42432 4.86641 2.47322C4.90603 2.52212 4.96002 2.55733 5.02075 2.57385C5.08148 2.59037 5.14586 2.58738 5.2048 2.5653C5.26373 2.54321 5.31422 2.50315 5.34913 2.45079C5.38404 2.39842 5.4016 2.3364 5.39932 2.27351C5.39704 2.21061 5.37504 2.15003 5.33643 2.10033C5.29782 2.05063 5.24456 2.01433 5.18418 1.99657C5.12381 1.97881 5.05937 1.98049 5 2.00136H4C3.46957 2.00136 2.96086 2.21208 2.58579 2.58715C2.21071 2.96222 2 3.47093 2 4.00136V9.00136C2 10.5927 2.63214 12.1188 3.75736 13.244C4.88258 14.3692 6.4087 15.0014 8 15.0014C9.5913 15.0014 11.1174 14.3692 12.2426 13.244C13.3679 12.1188 14 10.5927 14 9.00136V4.00136C14 3.47093 13.7893 2.96222 13.4142 2.58715C13.0391 2.21208 12.5304 2.00136 12 2.00136H11C10.9803 2.02106 10.9647 2.04445 10.954 2.07018C10.9434 2.09592 10.9379 2.12351 10.9379 2.15136C10.9379 2.17922 10.9434 2.20681 10.954 2.23254C10.9647 2.25828 10.9803 2.28166 11 2.30136C11.0197 2.32106 11.0431 2.33669 11.0688 2.34735C11.0946 2.35801 11.1221 2.36349 11.15 2.36349C11.1779 2.36349 11.2054 2.35801 11.2312 2.34735C11.2569 2.33669 11.2803 2.32106 11.3 2.30136"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8 15V16C8 17.5913 8.63214 19.1174 9.75736 20.2426C10.8826 21.3679 12.4087 22 14 22V22C15.5913 22 17.1174 21.3679 18.2426 20.2426C19.3679 19.1174 20 17.5913 20 16V12"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M20 12C21.1046 12 22 11.1046 22 10C22 8.89543 21.1046 8 20 8C18.8954 8 18 8.89543 18 10C18 11.1046 18.8954 12 20 12Z"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <h3 className="text-inherit font-body-font font-semibold text-[16px] leading-normal flex items-center gap-1">
              {isMobile || isTablet
                ? `${reassuranceCard3Title}*`
                : reassuranceCard3Title}
            </h3>
          </div>
          <p className="text-inherit hidden font-body-font font-normal text-[14px] leading-normal opacity-80 lg:block">
            {reassuranceCard3Description}
          </p>
        </div>
      </div>
    </div>
  );

  const renderHeader = () => {
    if (!referenceId) return null;

    if (showReassuranceTiles) {
      return renderReassuranceCards();
    }

    const greetingText = greeting || "Hi";

    if (isSimplifiedHeroBanner) {
      return (
        <div
          className={`lg:max-w-[832px] min-w-[280px] w-full flex flex-col items-center mx-auto  ${
            iSInternationalHeaderStyle
              ? "md:min-w-[624px]"
              : "md:max-w-[560px] min-h-[27px]"
          }`}
        >
          <div
            className={`mb-spacing-m text-dark-text-on-primary text-center w-[100%] ${
              iSInternationalHeaderStyle
                ? "lg:mb-spacing-xs md:mb-spacing-m"
                : "md:mb-spacing-[10px] "
            }`}
          >
            <p
              className={`text-2xs  leading-[24px] font-[600] ${
                iSInternationalHeaderStyle
                  ? "lg:text-s md:text-xs lg:leading-[23px] md:leading-[21px] min-h-[27px]"
                  : "md:text-xs md:leading-[21px]"
              }`}
            >
              {greetingText} {travellerName}, {subtitle}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="md:max-w-[590px] max-w-[280px] w-full flex flex-col items-center mx-auto">
        <div
          className={` mb-spacing-m text-dark-text-on-primary text-center w-[100%] ${
            iSInternationalHeaderStyle
              ? "lg:mb-spacing-xs md:mb-spacing-m"
              : "md:mb-spacing-l"
          }`}
        >
          <h1 className="mb-spacing-xs font-header-font md:text-[39px] md:leading-[44.85px] leading-[29.9px] text-[26px] text-center w-[100%] text-ellipsis overflow-hidden whitespace-nowrap">
            {greetingText}, {travellerName}
          </h1>
          <p className="text-xs leading-[27px]">{subtitle}</p>
        </div>
      </div>
    );
  };

  if (!headerContent) {
    return null;
  }
  return (
    <>
      <div data-testid="quoteHeader-test-id">{renderHeader()}</div>
      {!isSimplifiedHeroBanner && (
        <div className="border rounded-2xl border-[rgba(255,255,255,0.3)] bg-[rgba(39,36,39,0.24)] p-spacing-s lg:max-w-[590px] max-w-[100%] ">
          <section
            className="flex flex-col min-w-[240px] items-center font-body-font leading-[150%] text-[16px] md:mb-spacing-xxs mb-spacing-s min-h-[27px]"
            aria-label="Trip summary details"
          >
            {renderTripDetails()}
          </section>

          <div className="w-[100%] flex justify-center">
            {renderEditButton()}
          </div>
        </div>
      )}
      {isSimplifiedHeroBanner && (
        <div
          className={`border rounded-2xl  p-spacing-s border-[rgba(255,255,255,0.3)] bg-[rgba(39,36,39,0.24)]${
            iSInternationalHeaderStyle ? "md:mx-spacing-s md:p-spacing-xs " : ""
          }`}
        >
          <section
            className={`flex ${isMobile ? "flex-col" : "flex-row flex-wrap"} ${
              !isMobile && !isTabletRange && "md:flex-nowrap"
            } min-w-[240px] items-center font-body-font leading-[150%] text-[16px] min-h-[27px] md:justify-center ${
              iSInternationalHeaderStyle ? "md:pl-spacing-xs" : ""
            }`}
            aria-label="Trip summary details"
          >
            {renderTripDetails()}

            <div
              className={`h-fit flex items-center leading-none ${getButtonContainerClasses()} justify-center ${
                !isMobile && !isTabletRange && "md:justify-end"
              }`}
            >
              {renderEditButton()}
            </div>
          </section>
        </div>
      )}
      {dashboardEnabled && (
        <div data-testid="edit-quote-section">
          <SupportDrawer
            setOpen={setOpen}
            isOpen={open}
            onCloseAction={onCloseQuotePageOverlay}
          >
            <div>
              <EditTrip
                tripTitle={tripTitle}
                typeLabel={typeLabel}
                destinationLabel={destinationLabel}
                regionLabel={regionLabel}
                datesLabel={datesLabel}
                cruiseLabel={cruiseLabel}
                policyStartLabel={policyStartLabel}
                isOpen={openAccordion === "trip"}
                onToggle={() =>
                  setOpenAccordion(openAccordion === "trip" ? null : "trip")
                }
                brand={brand}
              />
              <EditTavellers
                travellerTitle={travellerTitle}
                travellersLabel={travellersLable}
                partyTypeLabel={partyTypeLable}
                addBtnLabel={addBtnLabel}
                partyTypeBtnLabel={partyTypeBtnLabel}
                editBtnLabel={editBtnLabel}
                isOpen={openAccordion === "travellers"}
                onToggle={() =>
                  setOpenAccordion(
                    openAccordion === "travellers" ? null : "travellers"
                  )
                }
                brand={brand}
              />
              <EditMedical
                medicalTitle={medicalTitle}
                medicalBtn={medicalBtn || ""}
                editMedicalBtn={editMedicalBtn || ""}
                isOpen={openAccordion === "medical"}
                onToggle={() =>
                  setOpenAccordion(
                    openAccordion === "medical" ? null : "medical"
                  )
                }
                brand={brand}
                newMedicalFlowEnabled
              />
              <EditContactDetails
                contactTitle={contactTitle}
                organizerTitle={organizerTitle}
                contactBtn={contactBtn || ""}
                editOrganizerBtn={editOrganizerBtn}
                isOpen={openAccordion === "contact"}
                onToggle={() =>
                  setOpenAccordion(
                    openAccordion === "contact" ? null : "contact"
                  )
                }
                brand={brand}
              />
            </div>
          </SupportDrawer>
        </div>
      )}
    </>
  );
}

export default withBrand(withDashboard(QuoteHeader));
